import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { BsPlus } from "react-icons/bs";
import { AppPageHeader } from "components/AppPageHeader";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getCities, getDarkStores, getSocieties } from "store/admin-slice/adminServicesSlice";
import {
  addDropPointSocieties,
  getDropPointSocieties,
  getDropPoints,
  removeDropPointSocieties,
  resetDropPointSocieties,
  resetMutationStatus,
} from "store/ppd/dropPointsSlice";
import { IDropPoint } from "common/interface/IDropPoint";
import { Society } from "common/interface/Society";
import { debounce } from "common/utils";
import { AddSocietiesModal } from "./component/AddSocietiesModal";
import { RemoveSocietiesConfirmModal } from "./component/RemoveSocietiesConfirmModal";

const { Text } = Typography;
const MESSAGE_KEY = "drop-points";

const DropPointsPage = () => {
  useDocumentTitle("Drop Point");
  const dispatch = useAppDispatch();

  const { darkStores, cities } = useAppSelector((state) => state.adminServices);
  const {
    dropPoints,
    dropPointsStatus,
    dropPointsError,
    societies,
    societiesStatus,
    societiesError,
    mutationStatus,
    mutationError,
  } = useAppSelector((state) => state.dropPoints);

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [selectedDropPoint, setSelectedDropPoint] = useState<IDropPoint>();
  const [selectedSocietyIds, setSelectedSocietyIds] = useState<number[]>([]);
  const [societyNameFilter, setSocietyNameFilter] = useState("");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const [isLoadingSocietyOptions, setIsLoadingSocietyOptions] = useState(false);
  const [societyOptions, setSocietyOptions] = useState<Society[]>([]);
  const debouncedSocietySearchRef = useRef<any>(null);

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
  }, [dispatch]);

  useEffect(() => {
    if (dropPointsStatus === "error") {
      message.error({ content: dropPointsError, key: MESSAGE_KEY, duration: 3 });
    }
  }, [dropPointsStatus, dropPointsError]);

  useEffect(() => {
    if (societiesStatus === "error") {
      message.error({ content: societiesError || "Failed to fetch societies", key: MESSAGE_KEY, duration: 3 });
    }
  }, [societiesStatus, societiesError]);

  useEffect(() => {
    if (mutationStatus === "saving") {
      message.loading({ content: "Saving...", key: MESSAGE_KEY });
    } else if (mutationStatus === "success") {
      message.success({ content: "Success!", key: MESSAGE_KEY, duration: 2 });
      setIsAddModalOpen(false);
      setIsRemoveModalOpen(false);
      setSelectedSocietyIds([]);
      if (selectedDropPoint) {
        dispatch(getDropPointSocieties({ dropPointId: selectedDropPoint.id }));
      }
      dispatch(resetMutationStatus());
    } else if (mutationStatus === "error") {
      message.error({ content: mutationError || "Something went wrong", key: MESSAGE_KEY, duration: 3 });
      dispatch(resetMutationStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationStatus, mutationError, dispatch]);

  useEffect(() => {
    debouncedSocietySearchRef.current = debounce((query: string) => {
      if (!storeId) return;
      setIsLoadingSocietyOptions(true);
      const filter = query ? `query=${query}&storeId=${storeId}` : `storeId=${storeId}`;
      dispatch(getSocieties({ pageNo: 1, pageSize: 50, filter }))
        .unwrap()
        .then((data) => {
          setSocietyOptions(data.data || []);
          setIsLoadingSocietyOptions(false);
        })
        .catch(() => {
          setIsLoadingSocietyOptions(false);
        });
    }, 400);
  }, [dispatch, storeId]);

  const handleCityChange = (value: string) => {
    setCityId(value);
    setStoreId(undefined);
    setSelectedDropPoint(undefined);
    dispatch(resetDropPointSocieties());
  };

  const handleStoreChange = (value: string) => {
    setStoreId(value);
    setSelectedDropPoint(undefined);
    dispatch(resetDropPointSocieties());
    dispatch(getDropPoints({ storeId: value }));
  };

  const handleSelectDropPoint = (dropPoint: IDropPoint) => {
    setSelectedDropPoint(dropPoint);
    setSelectedSocietyIds([]);
    setSocietyNameFilter("");
    dispatch(getDropPointSocieties({ dropPointId: dropPoint.id }));
  };

  const handleSocietySearch = (query: string) => {
    debouncedSocietySearchRef.current?.(query);
  };

  const handleOpenAddModal = () => {
    setSocietyOptions([]);
    setIsAddModalOpen(true);
  };

  const handleAddSocieties = (societyIds: number[]) => {
    if (!selectedDropPoint || societyIds.length === 0) return;
    dispatch(addDropPointSocieties({ dropPointId: selectedDropPoint.id, societyIds }));
  };

  const handleRemoveSocieties = () => {
    if (!selectedDropPoint || selectedSocietyIds.length === 0) return;
    dispatch(
      removeDropPointSocieties({ dropPointId: selectedDropPoint.id, societyIds: selectedSocietyIds })
    );
  };

  const filteredStores = (darkStores ?? []).filter((s) => s.cityId?.toString() === cityId);

  const filteredSocieties = societies.filter((society) =>
    society.societyName?.toLowerCase().includes(societyNameFilter.trim().toLowerCase())
  );

  const handleToggleOne = (societyId: number, checked: boolean) => {
    setSelectedSocietyIds((prev) =>
      checked ? [...prev, societyId] : prev.filter((id) => id !== societyId)
    );
  };

  const handleToggleAll = (checked: boolean) => {
    const filteredIds = filteredSocieties.map((s) => s.societyId);
    setSelectedSocietyIds((prev) =>
      checked
        ? Array.from(new Set([...prev, ...filteredIds]))
        : prev.filter((id) => !filteredIds.includes(id))
    );
  };

  const isAllFilteredSelected =
    filteredSocieties.length > 0 &&
    filteredSocieties.every((s) => selectedSocietyIds.includes(s.societyId));
  const isSomeFilteredSelected =
    filteredSocieties.some((s) => selectedSocietyIds.includes(s.societyId)) && !isAllFilteredSelected;

  const dropPointColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 70 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Latitude", dataIndex: "latitude", key: "latitude", width: 120 },
    { title: "Longitude", dataIndex: "longitude", key: "longitude", width: 120 },
    { title: "Travel Time (mins)", dataIndex: "travelTimeMins", key: "travelTimeMins", width: 160 },
  ];

  const societyColumns = [
    { title: "Society ID", dataIndex: "societyId", key: "societyId", width: 100 },
    { title: "Society Name", dataIndex: "societyName", key: "societyName" },
    { title: "Latitude", dataIndex: "latitude", key: "latitude", width: 120 },
    { title: "Longitude", dataIndex: "longitude", key: "longitude", width: 120 },
    {
      title: (
        <Checkbox
          checked={isAllFilteredSelected}
          indeterminate={isSomeFilteredSelected}
          onChange={(e) => handleToggleAll(e.target.checked)}
        />
      ),
      key: "select",
      width: 60,
      align: "center" as const,
      render: (_: unknown, record: { societyId: number }) => (
        <Checkbox
          checked={selectedSocietyIds.includes(record.societyId)}
          onChange={(e) => handleToggleOne(record.societyId, e.target.checked)}
        />
      ),
    },
  ];

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Drop Point" />
        </Col>
      </Row>

      <Form layout="vertical" className="mb-8">
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="City" className="mb-4">
              <Select
                showSearch
                placeholder="Select city"
                className="w-full"
                value={cityId}
                onChange={handleCityChange}
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                }
              >
                {(cities ?? []).map((city) => (
                  <Select.Option value={city.id?.toString()} key={city.id}>
                    {city.name} || {city.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {cityId && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Store" className="mb-4">
                <Select
                  showSearch
                  placeholder="Select store"
                  className="w-full"
                  value={storeId}
                  onChange={handleStoreChange}
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {filteredStores.map((store) => (
                    <Select.Option value={store.id?.toString()} key={store.id}>
                      {store.name} || {store.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {storeId && (
        <Row>
          <Col xs={24}>
            {dropPointsStatus === "success" && dropPoints.length === 0 ? (
              <Row justify="center" align="middle" style={{ minHeight: "200px" }}>
                <Col>
                  <Empty
                    description={
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        No Drop Points Found for this Store
                      </Text>
                    }
                  />
                </Col>
              </Row>
            ) : (
              <Table
                columns={dropPointColumns}
                dataSource={dropPoints}
                pagination={false}
                bordered
                size="small"
                loading={dropPointsStatus === "loading"}
                rowKey="id"
                rowClassName={(record) =>
                  record.id === selectedDropPoint?.id ? "ant-table-row-selected" : ""
                }
                onRow={(record) => ({
                  onClick: () => handleSelectDropPoint(record),
                  style: { cursor: "pointer" },
                })}
              />
            )}
          </Col>
        </Row>
      )}

      {selectedDropPoint && (
        <>
          <Row style={{ marginTop: 24 }}>
            <Col>
              <Text strong style={{ fontSize: 16 }}>
                Societies mapped to &quot;{selectedDropPoint.name}&quot;
              </Text>
            </Col>
          </Row>
          <Row>
            <AppActionStrip>
              <Button
                type="primary"
                className="action-strip__item"
                icon={<BsPlus />}
                onClick={handleOpenAddModal}
              >
                Add Societies
              </Button>
              <Button
                danger
                className="action-strip__item"
                icon={<DeleteOutlined />}
                disabled={selectedSocietyIds.length === 0}
                onClick={() => setIsRemoveModalOpen(true)}
              >
                Remove Selected
              </Button>
            </AppActionStrip>
          </Row>

          {societiesStatus === "success" && societies.length > 0 && (
            <Row style={{ marginBottom: 12 }}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search by society name"
                  prefix={<SearchOutlined />}
                  allowClear
                  value={societyNameFilter}
                  onChange={(e) => setSocietyNameFilter(e.target.value)}
                />
              </Col>
            </Row>
          )}

          {societiesStatus === "loading" ? (
            <Row justify="center" style={{ padding: "24px 0" }}>
              <Spin />
            </Row>
          ) : societiesStatus === "success" && societies.length === 0 ? (
            <Row justify="center" align="middle" style={{ minHeight: "160px" }}>
              <Col>
                <Empty
                  description={
                    <Text type="secondary" style={{ fontSize: 16 }}>
                      No societies mapped to this drop point
                    </Text>
                  }
                />
              </Col>
            </Row>
          ) : societiesStatus === "error" ? (
            <Row justify="center" align="middle" style={{ minHeight: "160px" }}>
              <Col>
                <Empty
                  description={
                    <Text type="secondary" style={{ fontSize: 16 }}>
                      {societiesError || "Failed to fetch societies"}
                    </Text>
                  }
                />
              </Col>
            </Row>
          ) : (
            <Table
              columns={societyColumns}
              dataSource={filteredSocieties}
              pagination={false}
              bordered
              size="small"
              rowKey="societyId"
              locale={{ emptyText: "No societies match your search" }}
            />
          )}
        </>
      )}

      <AddSocietiesModal
        open={isAddModalOpen}
        status={mutationStatus}
        societyOptions={societyOptions}
        isLoadingOptions={isLoadingSocietyOptions}
        alreadyMappedIds={societies.map((s) => s.societyId)}
        onSearch={handleSocietySearch}
        onCancel={() => setIsAddModalOpen(false)}
        onConfirm={handleAddSocieties}
      />

      <RemoveSocietiesConfirmModal
        open={isRemoveModalOpen}
        status={mutationStatus}
        count={selectedSocietyIds.length}
        onCancel={() => setIsRemoveModalOpen(false)}
        onConfirm={handleRemoveSocieties}
      />
    </>
  );
};

export default DropPointsPage;
